import { convertedQtyFormatterConfig } from '@/config/config';
import { mergeRefs, parseShortFormNumber } from '@/utils/common';
import useLanguage from '@/utils/hooks/useLanguage';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { ComponentProps, forwardRef, useEffect, useRef } from 'react';
import { useLocale, useNumberField } from 'react-aria';
import { useFormContext } from 'react-hook-form';
import { useNumberFieldState } from 'react-stately';
import PortalCurrencyIcon from '../common/PortalCurrencyIcon';

interface Props extends ComponentProps<'input'> {
    min?: number;
    step?: number;
    label?: string;
    className?: string;
    currency?: string;
    defaultValue?: string | number;
}

const PortalNumberInput = forwardRef<HTMLInputElement, Props>((props, ref: any) => {
    const { name = '', min = 0, step = 1, label, disabled, defaultValue, className, currency } = props;
    const { getValues, setValue, resetField, watch } = useFormContext();

    const initValue = +(defaultValue || 0);

    const inputRef = useRef(null);
    const { locale } = useLocale();
    const { dir } = useLanguage();

    const state = useNumberFieldState({ locale, formatOptions: convertedQtyFormatterConfig });
    const { groupProps, inputProps } = useNumberField({ label: label || name }, state, inputRef);

    const value = watch(name);

    useEffect(() => {
        if (value !== state.numberValue && !isNaN(state.numberValue)) {
            setValue(name, state.numberValue, { shouldValidate: true, shouldDirty: true });
        }
    }, [state.numberValue]);

    useEffect(() => {
        if (value !== state.numberValue) {
            const nextValue = BigNumber(value);
            const isDirty = !nextValue.isEqualTo(initValue);
            if (!isDirty) resetField(name);
            state.setNumberValue(value);
        }
    }, [value]);

    // useEffect(() => {
    //     if (defaultValue) setValue(name, defaultValue, { shouldValidate: true, shouldDirty: true });
    // }, [defaultValue]);

    return (
        <div {...groupProps} className="relative flex">
            <div
                className={cn(
                    'flex justify-center items-center flex-1 basis-0 text-sm text-brand-text bg-brand-background-dark border-brand-neutral-700',
                    {
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'pr-3 border border-r-0 rounded-r-md': dir === 'rtl',
                        'pl-3 border border-r-0 rounded-l-md': dir === 'ltr'
                    }
                )}>
                <PortalCurrencyIcon show={currency} disabled={disabled} />
            </div>
            <input
                {...inputProps}
                ref={mergeRefs(inputRef, ref)}
                value={state.inputValue}
                placeholder={props.placeholder}
                onKeyDown={(e) => {
                    if (['k', 'm'].includes(e.key)) {
                        e.preventDefault();
                        const val = parseShortFormNumber(state.numberValue, e.key);
                        if (!isNaN(val)) {
                            setValue(name, val, { shouldValidate: true, shouldDirty: true });
                            state.commit();
                        }
                    }
                }}
                autoComplete="off"
                className={cn(
                    'appearance-none block w-full p-2 bg-brand-background-dark border-brand-neutral-700 text-sm placeholder-brand-neutral-500 focus:outline-none',
                    {
                        // 'border-l-0': symbol,
                        // 'rounded-l-md': !symbol,
                        'text-brand-text': !className,
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'pl-3 border border-r-0 rounded-l-md': dir === 'rtl',
                        'pr-3 border border-l-0 rounded-r-md': dir === 'ltr'
                    },
                    className
                )}
                disabled={disabled}
            />
            {/* <div className="absolute right-2 flex flex-col h-full justify-center text-xs text-brand-text w-5">
                <button
                    type="button"
                    className={cn('px-0', {
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'hover:text-brand-green-light': !disabled
                    })}
                    onClick={() => {
                        if (!disabled) {
                            const prevValue = +(getValues(name) || 0);
                            const nextValue = BigNumber(prevValue).plus(step);
                            const isDirty = !nextValue.isEqualTo(initValue);
                            if (!isDirty) resetField(name);
                            setValue(name, nextValue.toNumber(), { shouldValidate: true, shouldDirty: isDirty });
                        }
                    }}>
                    &#x25B2;
                </button>
                <button
                    type="button"
                    className={cn('px-0', {
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'hover:text-brand-green-light': !disabled
                    })}
                    onClick={() => {
                        if (!disabled) {
                            const prevValue = +(getValues(name) || 0);
                            const nextValue = BigNumber(prevValue).minus(step);
                            if (nextValue.isLessThanOrEqualTo(min)) {
                                const isDirty = min !== initValue;
                                if (!isDirty) resetField(name);
                                setValue(name, min, { shouldValidate: true, shouldDirty: isDirty });
                            } else {
                                const isDirty = !nextValue.isEqualTo(initValue);
                                if (!isDirty) resetField(name);
                                setValue(name, nextValue.toNumber(), { shouldValidate: true, shouldDirty: isDirty });
                            }
                        }
                    }}>
                    &#x25BC;
                </button>
            </div> */}
        </div>
    );
});

PortalNumberInput.displayName = 'PortalNumberInput';

export default PortalNumberInput;
